import React, { useCallback, useState } from 'react';
import { Select, Typography } from 'antd';
import {
  buildConfigV1, nodeOperationsTableFieldNames,
  nodeStatusUnits, nodeStatusKeys
} from 'farmx-web-ui';
import { hooks } from 'farmx-redux-core';
import { MdDelete } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import Resizer from 'components/graph/Resizer';
import NewChartComponent from 'components/graph/NewChartComponent';
import {
  tooltipBgColor,
  tooltipBorderColor,
} from '../../../utils/colors';
import { updateChartHeight } from 'helper/common';

const { Option } = Select;

const defaultValues = ["rssi", "solarVoltage", "chargingCurrent", "batteryVoltage"];
const dataGapHours = 6;
const xAxisLabels = {
  y: 10,
  x: 1,
  align: 'left',
};

const NodeHealthGraph = (
  props: any
) => {
  const {
    items, onChange, onClear, values, graphData, startDate, endDate
  } = props;
  const [selectedValues, setSelectedValues] = useState<any>(values || defaultValues)
  const { t } = useTranslation();
  const getUserUnits = hooks.useUnits();
  
  const onClick = useCallback(() => {
    setSelectedValues([]);
    if (onClear) onClear();
  }, [onClear]);

  const filterOption = useCallback(
    (input: any, option: any) => option?.label?.toLowerCase().includes(input.toLowerCase()), []
  );

  const defaultConfig = {
    chart: {
      type: 'line',
      spacingTop: 20,
      zoomType: 'xy',
    },
    tooltip: {
      backgroundColor: tooltipBgColor,
      borderColor: tooltipBorderColor,
      style: {
        color: 'white',
      },
    },
    legend: {
      enabled: false,
    },
    xAxis: {
      type: 'datetime',
      ordinal: false,
      gridLineWidth: 1,
      labels: xAxisLabels,
      alternateGridColor: 'rgba(200,200,200,0.1)',
      min: startDate?.valueOf(),
      max: endDate?.valueOf(),
      tickInterval: null,
      dateTimeLabelFormats: {},
    },
    plotOptions: {}
  };

  function customOnChange(variables: any) {
    setSelectedValues(variables)
    if (onChange) onChange(variables);
  }

  const onResize = useCallback((event, chartHeight, wrapperElem) => {
    updateChartHeight(chartHeight, wrapperElem);
  }, []);

  return (
    <>
      <div className="health-variable-div graph-page-sensor-select">
        <div className="font-bold health-label graph-page-sensor-select-label">
          {`${t('Health Variables')} :`}
        </div>
        <Select
          showSearch
          filterOption={filterOption}
          mode="multiple"
          className="select-component"
          placeholder= {t("Select Variable")}
          value={selectedValues?.length ? selectedValues : undefined}
          onChange={customOnChange}
          optionLabelProp="label"
          allowClear
          maxTagCount="responsive"
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          {items?.map((item) => (
            <Option key={item} value={item} label={nodeOperationsTableFieldNames?.[item]}>
            <div>
                {nodeOperationsTableFieldNames?.[item]}
            </div>
            </Option>
          ))}
        </Select>
        <div
          className="graph-page-sensor-select-clear"
          role="button"
          onClick={onClick}
          tabIndex={0}
          onKeyDown={onClick}
        >
          <MdDelete size="1.5em" title="Clear All Selection" />
        </div>
      </div>
      {Object.keys(graphData || {})?.length
        ? selectedValues.map((variable) => {
            if (graphData?.[variable]) {
              const updatedData = Object.entries(graphData?.[variable] || {}).map(([key, value]) => {
                return { 
                    [key]: { 
                        variable: key,
                        data: value ? [value] : [],
                        units: value?.units,
                        originalUnit: value?.units,
                        actualChartKey: key,
                    }
                  }
              }).reduce((acc, obj) => ({ ...acc, ...obj}), {})
              const config = buildConfigV1(updatedData, false, true, dataGapHours, getUserUnits);
              const updatedYAxis = config.yAxis.map((item, index) => {
                const seriesItem = config.series?.[index];
                return { ...item, title: {
                  text: `${nodeOperationsTableFieldNames?.[seriesItem?.actualKey]} (${seriesItem
                    ?.originalUnit ? seriesItem?.originalUnit : nodeStatusUnits
                    ?.[nodeStatusKeys?.[seriesItem?.actualKey]]?.shortName || nodeStatusUnits
                    ?.[nodeStatusKeys?.[seriesItem?.actualKey]]})`,
                  labels: {
                    ...item.labels,
                    enabled: true,
                  }
                }}
              })
              const updatedSeries = config.series.map((item, index) => {
                return {
                  ...item,
                  name: `${nodeOperationsTableFieldNames?.[item?.actualKey]} (${item
                    ?.originalUnit ? item?.originalUnit : nodeStatusUnits
                    ?.[nodeStatusKeys?.[item?.actualKey]]?.shortName || nodeStatusUnits
                    ?.[nodeStatusKeys?.[item?.actualKey]]})`,
                  color: '#7cb5ec', // default line color
                }
              })
              const updatedConfig = { ...defaultConfig, series: updatedSeries, yAxis: updatedYAxis }
              return (
                <div className="chart-body">
                  <div className="chart-component-wrapper">
                    <Resizer onResize={onResize}>
                      <div className="chart-component-main">
                        <NewChartComponent
                          config={updatedConfig}
                          error={null}
                        />
                      </div>
                    </Resizer>
                  </div>
                </div>
              )
            }

            return null
          }) : (
          <div className="no-data-card text-center">
            <Typography.Text>{t('No Data')}</Typography.Text>
          </div>
        )}
    </>
  );
};

export default NodeHealthGraph;
